import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useWindowSize } from 'react-use';
import { demoPagesMenu } from '../../menu';
import useDarkMode from '../../hooks/useDarkMode';
import Icon from '../../components/icon/Icon';
import AuthContext from '../../contexts/authContext';
import ThemeContext from '../../contexts/themeContext';

// ── Helper: generate initials from name ──
function getInitials(name: string): string {
	const parts = name.trim().split(/\s+/);
	if (parts.length >= 2) {
		return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
	}
	return name.substring(0, 2).toUpperCase();
}

// ── Helper: consistent gradient based on name hash ──
function getNameGradient(name: string): string {
	const gradients = [
		'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
		'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
		'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
		'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
		'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
		'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
		'linear-gradient(135deg, #fccb90 0%, #d57eeb 100%)',
		'linear-gradient(135deg, #96fbc4 0%, #f9f586 100%)',
		'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
		'linear-gradient(135deg, #667eea 0%, #43e97b 100%)',
	];
	let hash = 0;
	for (let i = 0; i < name.length; i++) {
		hash = name.charCodeAt(i) + ((hash << 5) - hash);
	}
	return gradients[Math.abs(hash) % gradients.length];
}

// ── Helper: role → badge color class ──
function getRoleBadgeColor(role: string | null): string {
	const map: Record<string, string> = {
		SUPER_ADMIN: 'danger',
		LEGAL_HEAD: 'primary',
		LEGAL_APPROVER: 'info',
		LEGAL_STAFF: 'info',
		FINANCE: 'warning',
		ADMIN: 'secondary',
		USER: 'success',
	};
	if (!role) return 'secondary';
	return map[role] || 'info';
}

const User = () => {
	const { width } = useWindowSize();
	const { setAsideStatus } = useContext(ThemeContext);
	const { setUser } = useContext(AuthContext);
	const navigate = useNavigate();
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();
	const { t } = useTranslation(['translation', 'menu']);

	const getUserData = () => {
		const storedData = localStorage.getItem('userdata');
		if (storedData) {
			try {
				return JSON.parse(storedData);
			} catch (error) {
				console.error('Failed to parse userdata:', error);
				return null;
			}
		}
		return null;
	};

	const user = getUserData();
	const userName = user?.employe_name || user?.name || 'User';
	const branchName = user?.branch_name || '';
	const role = user?.role || null;
	const roleLabel = user?.role_label || role || '';

	const handleLogout = () => {
		localStorage.removeItem('token');
		localStorage.removeItem('menu');
		localStorage.removeItem('userdata');
		if (setUser) {
			setUser('');
		}
		if (width < Number(process.env.REACT_APP_MOBILE_BREAKPOINT_SIZE)) {
			setAsideStatus(false);
		}
		navigate(`../${demoPagesMenu.login.path}`);
	};

	return (
		<div className='user-card'>
			{/* ── Avatar + Nama + Role ── */}
			<div className='user-card-header'>
				<div
					className='user-card-avatar'
					style={{ background: getNameGradient(userName) }}>
					{getInitials(userName)}
				</div>
				<div className='user-card-info'>
					<div className='user-card-name' title={userName}>
						{userName}
					</div>
					{!!roleLabel && (
						<span
							className={classNames(
								'user-card-role',
								`user-card-role--${getRoleBadgeColor(role)}`,
							)}>
							{roleLabel}
						</span>
					)}
				</div>
			</div>

			{/* ── Branch ── */}
			{!!branchName && (
				<div className='user-card-branch' title={branchName}>
					<Icon icon='Business' className='user-card-branch-icon' />
					<span>{branchName}</span>
				</div>
			)}

			{/* ── Menu Items ── */}
			<div className='user-card-menu'>
				<div
					className='user-card-menu-item'
					role='presentation'
					onClick={() => navigate('/settings/profile')}>
					<Icon icon='AccountBox' className='user-card-menu-icon' />
					<span>{t('menu:Profile') as string}</span>
				</div>
				<div
					className='user-card-menu-item'
					role='presentation'
					onClick={() => setDarkModeStatus(!darkModeStatus)}>
					<Icon
						icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
						className='user-card-menu-icon'
						color={darkModeStatus ? 'info' : 'warning'}
					/>
					<span>
						{darkModeStatus
							? (t('menu:DarkMode') as string)
							: (t('menu:LightMode') as string)}
					</span>
				</div>
			</div>

			{/* ── Divider ── */}
			<div className='user-card-divider' />

			{/* ── Logout ── */}
			<div
				className='user-card-logout'
				role='presentation'
				onClick={handleLogout}>
				<Icon icon='Logout' className='user-card-logout-icon' />
				<span>{t('menu:Logout') as string}</span>
			</div>
		</div>
	);
};

export default User;
